// components/features/support/knowledge/KnowledgeArticleModal.tsx
import React from "react";
import { X, Copy } from "lucide-react";
import BaseModal from "../../../ui/Modal";
import { KnowledgeArticle } from "../../../../types/features/support/KnowledgeArticle";
import { alertSuccess } from "../../../../helpers/alertError";

interface KnowledgeArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  article: KnowledgeArticle | null;
}

export default function KnowledgeArticleModal({ isOpen, onClose, article }: KnowledgeArticleModalProps) {
  if (!article) return null;

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "Problème":
        return { label: "Problème", className: "bg-red-100 text-red-700" };
      case "Procédure Agent":
        return { label: "Procédure agent", className: "bg-blue-100 text-blue-700" };
      case "Procédure Technique":
        return { label: "Procédure technique", className: "bg-purple-100 text-purple-700" };
      case "Escalade":
        return { label: "Escalade", className: "bg-orange-100 text-orange-700" };
      case "Réponse Client":
        return { label: "Réponse client", className: "bg-green-100 text-green-700" };
      default:
        return { label: category || "Article", className: "bg-gray-100 text-gray-700" };
    }
  };

  const category = getCategoryLabel(article.category);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alertSuccess("Texte copié dans le presse-papier");
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={article.title} size="full">
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between pb-4 border-b">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full ${category.className}`}>{category.label}</span>
          </div>
        </div>

        {/* Objectif */}
        {article.objective && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">🎯 Objectif</h3>
            <p className="text-gray-600">{article.objective}</p>
          </div>
        )}

        {/* Description */}
        {article.description && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">📖 Description</h3>
            <p className="text-gray-600">{article.description}</p>
          </div>
        )}

        {/* Causes */}
        {article.causes && article.causes.length > 0 && (
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <h3 className="text-sm font-medium text-red-800 mb-2">🚨 Causes possibles</h3>
            <ul className="space-y-1">
              {article.causes.map((cause, idx) => (
                <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                  <span>•</span> {cause}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Signes d'identification */}
        {article.signs && article.signs.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="text-sm font-medium text-blue-800 mb-2">🔍 Signes d'identification</h3>
            <ul className="space-y-1">
              {article.signs.map((sign, idx) => (
                <li key={idx} className="text-sm text-blue-700 flex items-start gap-2">
                  <span>•</span> {sign}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Procédure Agent */}
        {article.agent_procedure && article.agent_procedure.length > 0 && (
          <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
            <h3 className="text-sm font-medium text-indigo-800 mb-2">👨‍💼 Procédure Agent</h3>
            <ul className="space-y-1">
              {article.agent_procedure.map((step, idx) => (
                <li key={idx} className="text-sm text-indigo-700 flex items-start gap-2">
                  <span>{idx + 1}.</span> {step}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Procédure Technique */}
        {article.technical_procedure && article.technical_procedure.length > 0 && (
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <h3 className="text-sm font-medium text-purple-800 mb-2">⚙️ Procédure Technique</h3>
            <ul className="space-y-1">
              {article.technical_procedure.map((step, idx) => (
                <li key={idx} className="text-sm text-purple-700 flex items-start gap-2">
                  <span>{idx + 1}.</span> {step}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Erreurs à éviter */}
        {article.errors_to_avoid && article.errors_to_avoid.length > 0 && (
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <h3 className="text-sm font-medium text-yellow-800 mb-2">❌ Erreurs à éviter</h3>
            <ul className="space-y-1">
              {article.errors_to_avoid.map((error, idx) => (
                <li key={idx} className="text-sm text-yellow-700 flex items-start gap-2">
                  <span>•</span> {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Escalade */}
        {article.escalation && article.escalation.to && (
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <h3 className="text-sm font-medium text-orange-800 mb-2">⬆️ Escalade</h3>
            <p className="text-sm font-medium text-orange-800">Vers : {article.escalation.to}</p>
            {article.escalation.when && article.escalation.when.length > 0 && (
              <p className="text-sm text-orange-700">Quand : {article.escalation.when.join(", ")}</p>
            )}
          </div>
        )}

        {/* Réponse officielle */}
        {article.client_response && (
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-sm font-medium text-purple-800">💬 Réponse officielle au client</h3>
              <button
                onClick={() => copyToClipboard(article.client_response)}
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
              >
                <Copy className="w-3 h-3" /> Copier
              </button>
            </div>
            <p className="text-sm text-purple-700 italic">"{article.client_response}"</p>
          </div>
        )}

        {/* Documentation Produit */}
        {article.product_doc && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">📚 Documentation Produit Associée</h3>
            <p className="text-gray-600 text-sm">{article.product_doc}</p>
          </div>
        )}

        {/* Département Responsable */}
        {article.responsible_department && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">🏢 Département Responsable</h3>
            <p className="text-gray-600 text-sm">{article.responsible_department}</p>
          </div>
        )}

        {/* Mots-clés */}
        {article.keywords && article.keywords.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">🏷️ Mots-clés</h3>
            <div className="flex flex-wrap gap-2">
              {article.keywords.map((keyword, idx) => (
                <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
}
